// client/src/features/auth/redirect.ts — the deep link across the login flow · AUTH-004.
//
// 04 §9.2.2: the `/app` guard sends an anonymous visitor to `/login?redirect=<href>`, and after
// login the `redirect` param restores the deep link (doc 05 AUTH-004 criterion 4). The value
// travels through every screen a login may pass — MFA, enrollment, organization selection —
// so each of them reads and forwards the same search param.

import { z } from 'zod';

import { isPortalTenant, type Session } from '@/session';

/** The search params every `_auth/*` screen accepts: an optional in-app path to return to. */
export const redirectSearchSchema = z.object({
  redirect: z.string().optional(),

  // The invitation whose address these screens must use, when an invitation sent the person
  // here (D-1930). It travels as the TOKEN and not as the e-mail: the address is then the
  // server's answer to that token rather than a value in a URL anybody can compose, so nobody
  // can hand somebody a link that locks a form to an address of their choosing.
  invite: z.string().optional(),
});

export type RedirectSearch = z.infer<typeof redirectSearchSchema>;

/** Where a signed-in user lands when nothing asked for anything more specific (04 §9.2). */
export const DEFAULT_LANDING = '/app/dashboard';

/** Where the tenant portal starts (04 §6.6). */
export const PORTAL_LANDING = '/portal';

/**
 * Where THIS account lands when nothing asked for anything more specific (D-1732).
 *
 * `DEFAULT_LANDING` is the owner console, and it is the wrong first screen for an account whose
 * only relationship to the product is a lease. Signup gives every person their own organization
 * and makes them its `owner` (D-1530), so somebody invited as a tenant arrives holding `owner`
 * over an empty portfolio: the role says landlord, the data says tenant. The data decides here.
 *
 * The test is „a lease, and no property of their own": a landlord who also rents — the account
 * „Chiriaș la rândul meu" exists for — keeps landing in the console, because their portfolio is
 * the thing they came for and the rental is a section inside it.
 *
 * This moves the LANDING only. `/app` stays reachable by typing it, deliberately: the account is
 * a real organization owner who may add a property at any moment, and a guard that bounced them
 * would make that impossible to start.
 */
export function landingFor(session: Session | null): string {
  if (!session) return DEFAULT_LANDING;
  if (session.role === 'tenant') return PORTAL_LANDING;

  return isPortalTenant(session) && session.ownedPropertyCount === 0
    ? PORTAL_LANDING
    : DEFAULT_LANDING;
}

/**
 * The four authenticated destinations a login may legitimately return to: the owner console
 * (04 §6.1), the tenant portal (§6.6), the invitation page (SCR-AUTH-006) and the platform
 * area (§8.19). PORTAL-002 added the portal - a tenant whose deep link into `/portal/meters`
 * expired would otherwise be dropped at `/app/dashboard`, which their own role's guard
 * immediately bounces back to `/portal`, losing the link on the way (D-1172).
 *
 * `/invite` is not a shell: it is one page under `_auth` that holds the token itself and needs
 * a session to spend it, so an anonymous visitor is sent to `/login` carrying
 * `redirect=/invite/<token>` and has to come back to the SAME token. Without the prefix here
 * that value is discarded and the person lands on the dashboard having lost the invitation
 * they clicked (D-1590).
 *
 * ADM-001 adds `/admin` for the portal's reason: a platform admin holds no organization
 * permission, so `/app/dashboard` is a screen they can never see (D-1642).
 *
 * `/preluare-proprietate` is `/invite`'s twin and joins for `/invite`'s reason: one page under
 * `_auth` holding a token it cannot spend without a session, whose visitor is sent to `/login`
 * or `/register` carrying `redirect=/preluare-proprietate/<token>` and has to come back to the
 * SAME offer. Without the prefix here the value is discarded and they land on the dashboard
 * having lost the apartment they were being handed (D-1902).
 */

/**
 * Where a platform admin lands. They hold no organization-scoped permission (02 §6.7), so
 * every screen under `/app` would refuse them - `DEFAULT_LANDING` is not a landing for this
 * caller at all, and `/app`'s guard sends them here instead of rendering SCR-SYS-403 twenty-two
 * times over (D-1642).
 */
export const PLATFORM_LANDING = '/admin/accounts';

const ALLOWED_PREFIXES = [
  '/app',
  '/portal',
  '/invite',
  '/admin',
  '/preluare-proprietate',
] as const;

/**
 * A `redirect` value the app will actually follow: a same-origin, in-app path. Anything else —
 * an absolute URL, a protocol-relative `//evil`, a path outside the shells above — is
 * discarded, so the login page can never be used to bounce a user off the site.
 *
 * `/link/*` is deliberately NOT allowed: those pages have no login and nothing to return to
 * (04 §6.7), so a `redirect` naming one could only have been put there by someone else. The
 * invitation page is the opposite case — it is useless WITHOUT a session, and the token in it
 * is redeemable only by the address it was issued to (D-198), so a forwarded link admits
 * nobody. The hand-over page is that case again, and the binding is the stronger one: an
 * addressed offer is refused outright for any other account (D-1900).
 */
export function safeRedirect(candidate: string | undefined, fallback?: string): string {
  // The fallback is what a REJECTED or absent deep link falls back to, and every caller that has
  // a session to hand passes `landingFor(session)` so that a tenant-only account is not dropped
  // on the owner dashboard (D-1732). Callers with no session keep the console default.
  return returnPath(candidate) ?? fallback ?? DEFAULT_LANDING;
}

/**
 * The deep link when `safeRedirect` would follow it, and `undefined` when it would not — the
 * same test, answered without a fallback.
 *
 * It exists for the return path a confirmation link carries (D-2762). The signup, the login's
 * resend and the confirmation page's own resend send the deep link to the server as `returnTo`,
 * and the server puts it in the mail as `?next=` — so a value the login would discard must not
 * travel at all. Sending `safeRedirect`'s FALLBACK instead would be worse than sending nothing:
 * `/app/dashboard` written into the link would override `landingFor` at the first sign-in and
 * drop a tenant-only account on the owner console (D-1732).
 */
export function returnPath(candidate: string | undefined): string | undefined {
  if (!candidate || candidate.startsWith('//')) return undefined;
  if (!ALLOWED_PREFIXES.some((prefix) => candidate.startsWith(prefix))) return undefined;
  if (/[\s\\]/.test(candidate)) return undefined;
  return candidate;
}

/**
 * The invitation token inside a return path, when the path is an invitation page (D-2762).
 *
 * The confirmation link carries only `next`, and the login it leads to wants `invite` as well,
 * so the address the invitation names is filled in and locked the way D-1930 has it. The token
 * is the path's last segment — `AcceptInvitation` builds the path with `encodeURIComponent`, so
 * it is decoded once here — and nothing else is read out of the value. A hand-over path yields
 * nothing: its page locks no form.
 */
export function invitationTokenOf(path: string | undefined): string | undefined {
  const match = /^\/invite\/([^/?#]+)$/.exec(returnPath(path) ?? '');
  if (!match?.[1]) return undefined;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escape is not a token anybody issued.
    return undefined;
  }
}

/** Forwards the deep link to the next screen of the flow — as an absent KEY when there is none (`exactOptionalPropertyTypes`). */
export function forwardRedirect(search: RedirectSearch): RedirectSearch {
  return {
    ...(search.redirect ? { redirect: search.redirect } : {}),

    // The invitation travels with it: „Creează un cont" and „Intră în cont" are two halves of
    // one flow, and a person who takes the wrong one first must not lose the address on the
    // way (D-1930).
    ...(search.invite ? { invite: search.invite } : {}),
  };
}
